import { useNavigate, useSearch } from '@tanstack/react-router';
import { ReactNode } from 'react';

import { UserGatewayMessage } from '../../../../types/UserGatewayMessage';
import { ClearFilters } from '../../../cdl/Filter/ClearFilters/ClearFilters';
import { Filter } from '../../../cdl/Filter/DynamicFilterContainer/DynamicFilterContainer';
import {
    FilterMultiSelectOption,
    FilterMultiSelectShort,
} from '../../../cdl/Filter/FilterMultiSelectShort/FilterMultiSelectShort';
import { FilterText } from '../../../cdl/Filter/FilterText/FilterText';
import { Text } from '../../../cdl/Text/Text';
import { identifyUserRole } from '../../../common/helpers/identifyUserRole.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { useEnums } from '../../../common/hooks/useEnums';
import { useRole } from '../../../common/hooks/useRole';
import { Box } from '../../../common/ui/Box';

interface MembersFilterPillsProps {
    allEntries: UserGatewayMessage[];
    children: (filteredItems: UserGatewayMessage[]) => ReactNode;
}

export const MembersFilterPills = ({ allEntries, children }: MembersFilterPillsProps) => {
    const role = useRole();
    const { getHumanReadableValue } = useEnums();
    const navigate = useNavigate({
        from: role.isCustomer() ? '/customer-groups/$groupId/members/' : '/supplier-groups/$groupId/members/',
    });
    const search = useSearch({
        from: role.isCustomer() ? '/_app/customer-groups/$groupId/members/' : '/_app/supplier-groups/$groupId/members/',
    });
    const clearAllFilters = () => {
        navigate({
            search: (prevSearch) => ({
                ...prevSearch,
                filter: undefined,
            }),
        });
    };

    const roleFilter: Filter<string, UserGatewayMessage> = {
        filterFn: (values, entry) => {
            const userRole = identifyUserRole(entry.accessLevel, entry.companyAccesses);
            return values.includes(userRole);
        },
        component: (values, setValues) => {
            const availableRoles = Array.from(
                new Set(allEntries.map((user) => identifyUserRole(user.accessLevel, user.companyAccesses)))
            );
            const roleOptions: FilterMultiSelectOption<string>[] = availableRoles.map((role) => ({
                label: getHumanReadableValue('role', role),
                value: role,
            }));

            return (
                <FilterMultiSelectShort
                    label={translate('user.role')}
                    filterByLabel={translate('members.filters.filterByRole')}
                    options={roleOptions}
                    values={values}
                    onChange={setValues}
                />
            );
        },
    };

    const companiesFilter: Filter<string, UserGatewayMessage> = {
        filterFn: (values, entry) => {
            if (values.length === 0) {
                return true;
            }
            const userCompanyIds = entry.companyAccesses?.map((access) => access.companyId) ?? [];
            return values.some((companyId) => userCompanyIds.includes(companyId));
        },
        component: (values, setValues) => {
            const allCompanies = Array.from(
                new Map(
                    allEntries
                        .flatMap((user) => user.companyAccesses ?? [])
                        .filter((access) => access.company)
                        .map((access) => [access.company!.id, access.company!])
                ).values()
            );

            const companyOptions: FilterMultiSelectOption<string>[] = allCompanies.map((company) => ({
                label: company.internalName || company.name,
                value: company.id,
            }));

            return (
                <FilterMultiSelectShort
                    label={translate('user.companyColumn')}
                    filterByLabel={translate('demand.filter.company.filterByCompany')}
                    options={companyOptions}
                    values={values}
                    onChange={setValues}
                />
            );
        },
    };

    const nameFilter: Filter<string, UserGatewayMessage> = {
        filterFn: (values, entry) => {
            if (values.length === 0) {
                return true;
            }
            const fullName = `${entry.firstname || ''} ${entry.lastname || ''}`.trim().toLowerCase();
            return values.some((value) => fullName.includes(value.toLowerCase()));
        },
        component: (values, setValues) => {
            return (
                <FilterText
                    label={translate('user.name')}
                    filterByLabel={translate('members.filters.filterByName')}
                    value={values[0]}
                    onChange={(value) => setValues(value ? [value] : [])}
                />
            );
        },
    };

    const emailFilter: Filter<string, UserGatewayMessage> = {
        filterFn: (values, entry) => {
            if (values.length === 0) {
                return true;
            }
            return values.some((value) => entry.email.toLowerCase().includes(value.toLowerCase()));
        },
        component: (values, setValues) => {
            return (
                <FilterText
                    label={translate('user.mail')}
                    filterByLabel={translate('members.filters.filterByEmail')}
                    value={values[0]}
                    onChange={(value) => setValues(value ? [value] : [])}
                />
            );
        },
    };

    const filters = {
        name: nameFilter,
        email: emailFilter,
        role: roleFilter,
        companies: companiesFilter,
    };

    const filteredEntries: UserGatewayMessage[] = allEntries.filter((entry) => {
        return Object.entries(filters).every(([key, filter]) => {
            const filterValues = search.filter?.[key] ?? [];
            if (filterValues.length === 0) {
                return true;
            }

            return filter.filterFn(filterValues, entry);
        });
    });

    return (
        <>
            <Box display="flex" gap={4} alignItems="center">
                {Object.entries(filters).map(([key, filter]) => {
                    return (
                        <span key={key}>
                            {filter.component(search.filter?.[key] ?? [], (updatedValues) => {
                                const newFilterValue = updatedValues.length > 0 ? updatedValues : undefined;
                                const updatedFilter = {
                                    ...search.filter,
                                    [key]: newFilterValue,
                                };

                                // Only set filter object if at least one filter has values
                                const hasAnyFilters = Object.values(updatedFilter).some(
                                    (value) => Array.isArray(value) && value.length > 0
                                );

                                navigate({
                                    search: (prevSearch) => ({
                                        ...prevSearch,
                                        filter: hasAnyFilters ? updatedFilter : undefined,
                                    }),
                                });
                            })}
                        </span>
                    );
                })}
                {Object.values(search.filter ?? {}).some((it) => Array.isArray(it) && it.length > 0) ? (
                    <ClearFilters onClearClick={clearAllFilters} />
                ) : null}
            </Box>

            {children(filteredEntries)}

            {filteredEntries.length > 0 ? (
                <Box>
                    <Text variant="extraSmall" fontWeight={700} color="foreground.muted">
                        {filteredEntries.length}
                    </Text>
                    {Characters.EN_SPACE}
                    <Text variant="extraSmall" color="foreground.muted">
                        {translate('members.results')}
                    </Text>
                </Box>
            ) : null}
        </>
    );
};
